import { ChangeEvent, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useMediaQuery } from 'usehooks-ts'

import {
  useCheckIsEmailUsedMutation,
  useCustomerProfileFieldsQuery,
  useRegisterCustomerMutation,
} from '@entities/User/api/profile.gql'

import { useAuth, useProfileCustomerModal } from '@features/auth'
import { useErrorInfoModal } from '@features/modal'

import { URL_PAGE_POLICY } from '@shared/contants'
import { LocalizedLink as Link } from '@shared/lib/locale'
import Button from '@shared/ui/button'
import Checkbox from '@shared/ui/checkbox'
import ErrorMessage from '@shared/ui/error-message'
import { FormItem } from '@shared/ui/form'
import Input from '@shared/ui/input'
import InputDate from '@shared/ui/input-date'
import LoaderCircle from '@shared/ui/loader-circle'
import Select from '@shared/ui/select'
import flattenProfileInfo from '@shared/uttils/flattenProfileInfo'

import styles from './styles.module.scss'

export interface ProfileInfoForm {
  profileInfo: {
    [key: string]: string | boolean | null | undefined;
  };
  acceptPolicy: boolean;
}

function SkeletonLoading () {
  return (
    <SkeletonTheme>
      <Skeleton
        count={6}
        width={'100%'}
        height={30}
        style={{
          borderRadius: 6,
          marginBottom: 20,
        }}
      />
    </SkeletonTheme>
  )
}

export default function ProfileCustomerInfo () {
  const { openErrorInfoModal } = useErrorInfoModal()
  const isTablet = useMediaQuery('(max-width: 991px)')
  const { t: te } = useTranslation('errors')
  const { t: tv } = useTranslation('validation')
  const { t: tc } = useTranslation('common')
  const { t: ta } = useTranslation('auth')
  const { data, loading } = useCustomerProfileFieldsQuery()
  const [ registerCustomer ] = useRegisterCustomerMutation()
  const [ fetchCheckEmail, { loading: checkEmailLoading } ] =
    useCheckIsEmailUsedMutation()
  const [ isCorrectEmail, setIsCorrectEmail ] = useState(true)
  const { closeProfileCustomerModal } = useProfileCustomerModal()
  const { refetchUser } = useAuth()

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    setError,
  } = useForm<ProfileInfoForm>({
    defaultValues: {
      profileInfo: {},
    },
    mode: 'onChange',
  })

  const REQUIRED_MESSAGE = tv('required')
  const EMAIL_MESSAGE = tv('email')

  const onSubmit = async (dto: ProfileInfoForm) => {
    try {
      const answers = flattenProfileInfo(dto.profileInfo).map((i) => ({
        fieldName: i.fieldName,
        id: data?.customerProfileFields?.find(
          (f) => f?.fieldName === i.fieldName,
        )?.id as string,
        value: String(i.value),
      }))

      await registerCustomer({
        variables: { answers },
      })

      await refetchUser()
      closeProfileCustomerModal()
    }
    catch (error) {
      console.log('Customer profile error: ', error)
      openErrorInfoModal({
        description: te('create_profile'),
      })
    }
  }

  const watchedEmail = watch('profileInfo.email')

  useEffect(() => {
    if (!watchedEmail || errors.profileInfo?.email?.message) {
      setIsCorrectEmail(true)
      return
    }

    const timeout = setTimeout(async () => {
      try {
        const res = await fetchCheckEmail({
          variables: { email: watchedEmail as string },
        })
        const isUsed = res.data?.checkIsEmailUsed
        setIsCorrectEmail(!isUsed)

        if (isUsed) {
          setError('profileInfo.email', {
            type: 'manual',
            message: ta('error.email.taken'),
          })
        }
      }
      catch (_error) {
        setIsCorrectEmail(false)
        openErrorInfoModal({
          description: te('check_email'),
        })
      }
    }, 500)

    return () => clearTimeout(timeout)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ watchedEmail, errors.profileInfo?.email?.message ])

  if (loading) {
    return <SkeletonLoading />
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      {data?.customerProfileFields?.map((item) => {
        const fieldName = item?.fieldName
        if (!fieldName) return null

        const isMiddleNameField = fieldName === 'middleName' // [1;31mПроверка отчества[0m

        if (item?.type === 'string') {
          return (
            <FormItem key={item.id}>
              <Input
                placeholder={item.label || ''}
                inputSize={!isTablet ? 'extra_large' : 'medium'}
                {...register(`profileInfo.${fieldName}`, {
                  required:
                    item.required || isMiddleNameField
                      ? REQUIRED_MESSAGE
                      : false,
                })}
                isInvalid={!!errors?.profileInfo?.[fieldName]?.message}
              />
              {errors.profileInfo?.[fieldName]?.message && (
                <ErrorMessage>
                  {String(errors.profileInfo[fieldName]?.message)}
                </ErrorMessage>
              )}
            </FormItem>
          )
        }

        if (item?.type === 'email') {
          return (
            <FormItem key={item.id}>
              <Input
                placeholder={item.label || ''}
                inputSize={!isTablet ? 'extra_large' : 'medium'}
                {...register(`profileInfo.${fieldName}`, {
                  required: REQUIRED_MESSAGE,
                  pattern: {
                    value:
                      /^(?:[a-zA-Z0-9-_]+(?:\.[a-zA-Z0-9-_]+)*|"(...)")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}|(?:\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(?:\.(?!$)|$)){4}\]))$/,
                    message: EMAIL_MESSAGE,
                  },
                })}
                endDecorator={
                  checkEmailLoading && (
                    <span className={styles['input-decorator-end']}>
                      <LoaderCircle />
                    </span>
                  )
                }
                isInvalid={
                  !!errors?.profileInfo?.[fieldName]?.message || !isCorrectEmail
                }
              />
              {errors.profileInfo?.[fieldName]?.message && (
                <ErrorMessage>
                  {String(errors.profileInfo[fieldName]?.message)}
                </ErrorMessage>
              )}

              {!isCorrectEmail && (
                <ErrorMessage>{ta('error.email.taken')}</ErrorMessage>
              )}
            </FormItem>
          )
        }

        if (item?.type === 'select') {
          const options =
            item.fixedAnswers?.map((answer) => ({
              name: answer?.name || '',
              value: answer?.value || '',
            })) || []

          return (
            <FormItem key={fieldName}>
              <Controller
                name={`profileInfo.${fieldName}`}
                control={control}
                defaultValue=""
                rules={{ required: item.required ? REQUIRED_MESSAGE : false }}
                render={({ field }) => (
                  <Select
                    options={options}
                    placeholder={item.label}
                    value={field.value as string}
                    handleOption={field.onChange}
                    inputSize={!isTablet ? 'extra_large' : 'medium'}
                  />
                )}
              />
              {errors.profileInfo?.[fieldName]?.message && (
                <ErrorMessage>
                  {String(errors.profileInfo[fieldName]?.message)}
                </ErrorMessage>
              )}
            </FormItem>
          )
        }

        if (item.type === 'boolean') {
          return (
            <FormItem key={fieldName}>
              <Controller
                name={`profileInfo.${fieldName}`}
                control={control}
                defaultValue={item.defaultValue}
                render={({ field }) => (
                  <Checkbox
                    checkboxSize="extra_small"
                    label={item.label}
                    checked={!!field.value}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      field.onChange(e.target.checked)
                    }
                  />
                )}
              />
              {errors.profileInfo?.[fieldName]?.message && (
                <ErrorMessage>
                  {String(errors.profileInfo[fieldName]?.message)}
                </ErrorMessage>
              )}
            </FormItem>
          )
        }

        if (item.type === 'date') {
          return (
            <FormItem key={fieldName}>
              <Controller
                name={`profileInfo.${fieldName}`}
                control={control}
                rules={{ required: item.required ? REQUIRED_MESSAGE : false }}
                render={({ field }) => (
                  <InputDate
                    value={field.value as Date | null | undefined}
                    onChange={field.onChange}
                    placeholder={`${item.label}`}
                    inputSize={!isTablet ? 'extra_large' : 'medium'}
                  />
                )}
              />
              {errors.profileInfo?.[fieldName]?.message && (
                <ErrorMessage>
                  {String(errors.profileInfo[fieldName]?.message)}
                </ErrorMessage>
              )}
            </FormItem>
          )
        }

        return null
      })}

      <FormItem>
        <Controller
          name="acceptPolicy"
          control={control}
          defaultValue={false}
          rules={{ required: REQUIRED_MESSAGE }}
          render={({ field }) => (
            <Checkbox
              checkboxSize="extra_small"
              label={
                <span className={styles.root}>
                  {tc('policy.form.1', {
                    button: tc('continue'),
                  })}
                  <Link to={URL_PAGE_POLICY} target="_blank">
                    {tc('policy.form.2')}
                  </Link>
                </span>
              }
              checked={field.value}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                field.onChange(e.target.checked)
              }
            />
          )}
        />
        {errors.acceptPolicy && (
          <ErrorMessage>{errors.acceptPolicy.message}</ErrorMessage>
        )}
      </FormItem>

      <Button
        className={styles.button}
        type="submit"
        size="large"
        disabled={checkEmailLoading || !isCorrectEmail}
      >
        {tc('continue')}
      </Button>
    </form>
  )
}
